// src/stores/useMedicalRequestStore.ts
import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

import { PatientDetails, ProxyDetails, RequestReporterType } from '../types';
import { submitMedicalRequestApi } from '../services/medical-request.service';
import { useVerifyPatientDetails } from './useVerifyPatientDetails';
import { useVerifyNhsLogin } from './useVerifyNhsLogin';

export const useMedicalRequestStore = defineStore('medical-request', () => {
  const expiryTime = ref<Date | null>(null); // +1 hour

  const apiLoading = ref<boolean>(false);
  const setApiLoading = () => (apiLoading.value = true);
  const setApiDone = () => (apiLoading.value = false);

  const isEmergency = ref<boolean>(false);
  const reporterType = ref<RequestReporterType | null>(null);
  // unique after here, above can be made modular and shared

  const proxyDetails = ref<ProxyDetails | null>();
  const patientDetails = ref<PatientDetails | null>(null);
  const isValidPatient = ref<boolean>(false);
  const isVerified = ref<boolean>(false);
  const isNhsVerified = ref<boolean>(false);

  const problemDescription = ref<string>('');
  const problemRecentContact = ref<string>('');

  const attachments = ref<File[]>([]);

  const isExpired = computed(() => Date.now() > expiryTime?.value?.getTime());
  const isProxy = computed(() => reporterType.value === 'PROXY');

  function startNew() {
    attachments.value = [];
    expiryTime.value = new Date(Date.now() + 60 * 60 * 1000); // +1 hour
    isEmergency.value = false;
    isNhsVerified.value = false;
    isValidPatient.value = false;
    isVerified.value = false;
    patientDetails.value = null;
    problemDescription.value = '';
    problemRecentContact.value = '';
    proxyDetails.value = null;
    reporterType.value = null;
  }

  function endCurrent() {
    startNew();
    expiryTime.value = null;
  }

  function setIsEmergency(value: boolean) {
    isEmergency.value = value;
  }

  function setIsValidPatient(value: boolean) {
    isValidPatient.value = value;
  }

  function setIsNhsVerified(value: boolean = true) {
    isNhsVerified.value = value;
  }

  function setReporterType(value: RequestReporterType) {
    reporterType.value = value;
  }

  function setProxyDetails(details: ProxyDetails) {
    proxyDetails.value = details;
  }

  const verifyPatientDetails = useVerifyPatientDetails({
    patientDetails,
    isProxy: isProxy,
    setApiLoading,
    setApiDone,
    setIsValidPatient,
  });

  const verifyNhsLogin = async (code: string, csrfToken: string) => {
    const run = useVerifyNhsLogin({
      code,
      csrfToken,
      setApiLoading,
      setApiDone,
      setIsValidPatient,
      setIsNhsVerified,
      setPatientDetails,
    });

    const result = await run();
    if (result.ok) {
      setReporterType('PATIENT');
    }
    return result;
  };

  async function submitAction() {
    setApiLoading();
    try {
      const result = await submitMedicalRequestApi({
        reporterType: reporterType.value,
        proxyDetails: proxyDetails.value,
        patientDetails: patientDetails.value,
        problemDescription: problemDescription.value,
        problemRecentContact: problemRecentContact.value,
        attachments: attachments.value,
      });

      if (!result.ok) {
        return {
          ok: false,
          errors: result.errors,
        };
      }

      endCurrent();
      return { ok: true };
    } catch (e) {
      return { ok: false, errors: e.data?.errors };
    } finally {
      setApiDone();
    }
  }

  function setPatientDetails(details: PatientDetails | null) {
    patientDetails.value = details;
  }

  function setProblemDescription(text: string) {
    problemDescription.value = text;
  }

  function setProblemRecentContact(text: string) {
    problemRecentContact.value = text;
  }

  function addAttachment(file: File) {
    if (attachments.value.length >= 10) return;
    attachments.value.push(file);
  }

  function addAttachments(files: File[]) {
    attachments.value = files;
  }

  function removeAttachment(index: number) {
    attachments.value.splice(index, 1);
  }

  function setNhsSsoDetails(patientDetails: PatientDetails) {
    // NB. only patients verified via NHS App use this.
    setIsNhsVerified(true);
    // like this - setIsNhsApp(true);
    setPatientDetails(patientDetails);
    setIsValidPatient(true);
    setReporterType('PATIENT');
  }

  // ---- Expose flat state ----
  return {
    // apiLoading,
    apiLoading,
    // state
    expiryTime,

    //
    isEmergency,
    reporterType,
    proxyDetails,
    patientDetails,
    isProxy,
    isValidPatient,
    isVerified,
    isNhsVerified,
    problemDescription,
    problemRecentContact,
    attachments,

    // getters
    isExpired,

    // actions
    addAttachment,
    addAttachments,
    endCurrent,
    removeAttachment,
    setProblemDescription,
    setIsEmergency,
    setPatientDetails,
    setProxyDetails,
    setProblemRecentContact,
    setReporterType,
    setNhsSsoDetails: setNhsSsoDetails,
    startNew,
    submitAction,
    //
    verifyPatientDetails,
    verifyNhsLogin,
  };
});
